import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type ApiError, type NotificationsResult } from '@whinerp/client';
import { Button, InlineAction, Skeleton } from '@whinerp/ui';
import { useT } from '../app/i18n.js';
import { Overlay } from './Overlay.js';

/**
 * The notification panel (F-022).
 *
 * Grouped today/earlier, because "when" is the first thing somebody sorts by
 * in their head and a flat list of timestamps makes them do it themselves.
 *
 * Muting is one click from the notification. The brief says not to bury it in
 * preferences, and it is right: somebody who wants to stop hearing about a
 * thing is looking at the thing.
 */
export function NotificationPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();

  const notifications = useQuery<NotificationsResult, ApiError>({
    queryKey: ['notifications'],
    queryFn: () => api.notifications({ query: { limit: 30 } }),
    enabled: open,
  });

  const markAll = useMutation({
    mutationFn: () => api.markNotificationsRead({ body: { notificationId: null } }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const mute = useMutation({
    mutationFn: (about: { objectKey: string; recordId: string }) =>
      api.muteNotifications({ body: { ...about, muted: true } }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = notifications.data?.items ?? [];
  const groups = [
    { key: 'today' as const, label: t('notify.today') },
    { key: 'earlier' as const, label: t('notify.earlier') },
  ];

  return (
    <Overlay open={open} onClose={onClose} label={t('notify.title')}>
      <div data-component="NotificationPanel" className="flex flex-col">
        <div className="flex items-center justify-between border-b border-b-rule px-4 py-3">
          <p className="text-lead text-ink">{t('notify.title')}</p>
          <Button intent="ghost" size="small" data-notify-mark-all onClick={() => markAll.mutate()}>
            {t('notify.markAll')}
          </Button>
        </div>

        <div className="flex flex-col gap-3 p-4">
          {notifications.isPending ? (
            <Skeleton className="h-block-sm w-full" />
          ) : items.length === 0 ? (
            <p data-notify-empty className="text-dense text-ink-meta">
              {t('notify.none')}
            </p>
          ) : (
            groups.map((group) => {
              const inGroup = items.filter((item) => item.when === group.key);
              if (inGroup.length === 0) return null;
              return (
                <section key={group.key} data-notify-group={group.key} className="flex flex-col gap-1">
                  <p className="text-micro uppercase tracking-label text-ink-label">{group.label}</p>
                  <ul className="flex flex-col">
                    {inGroup.map((item) => (
                      <li
                        key={item.notificationId}
                        data-notify-item={item.notificationId}
                        data-notify-read={item.read ? 'yes' : 'no'}
                        className="flex flex-col gap-0.5 border-b border-b-rule py-2"
                      >
                        <div className="flex items-baseline gap-2">
                          {/* Unread is a mark, not only a weight: a bold row
                              beside a slightly less bold row is not a
                              difference people see. */}
                          {!item.read ? (
                            <span
                              aria-hidden
                              className="mt-1 inline-block h-chip w-chip rounded-chip bg-bronze"
                            />
                          ) : null}
                          {item.href ? (
                            <Link
                              to={item.href}
                              onClick={onClose}
                              data-notify-link={item.notificationId}
                              className="text-body text-ink underline decoration-transparent hover:decoration-bronze-rule"
                            >
                              {item.title}
                            </Link>
                          ) : (
                            <span className="text-body text-ink">{item.title}</span>
                          )}
                        </div>
                        {item.body ? <p className="text-meta text-ink-meta">{item.body}</p> : null}
                        {item.objectKey && item.recordId ? (
                          <InlineAction
                            data-notify-mute={item.notificationId}
                            onClick={() =>
                              mute.mutate({ objectKey: item.objectKey!, recordId: item.recordId! })
                            }
                          >
                            {t('notify.mute')}
                          </InlineAction>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })
          )}
        </div>
      </div>
    </Overlay>
  );
}
